import React, { useContext, useMemo } from 'react';
import { Avatar, Box } from '@mui/material';
import { MessageResponse } from '@ujet/websdk-headless';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ContentWithIndexedSources } from './components/ContentWithIndexedSources';
import { ContentWithSources } from './components/ContentWithSources';
import { ChatbotWidgetContext } from 'components/ChatbotWidget/utils/ChatbotWidgetContext';
import styles from './TextMessage.module.scss';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { Card, Typography } from 'gd-design-library';

const cx = classNames.bind(styles);

const CN = 'text-message';
const columnClass = `${CN}__column`;
const cardClass = `${CN}__card`;
const agentNameClass = `${CN}__agent-name`;
const textClass = `${CN}__text`;

export interface SourceInfo {
  title: string;
  link: string;
  body: string;
}

export interface MessageContentWithSources {
  text: string;
  sources?: SourceInfo[];
  indexed_sources?: {
    index: number;
    items: SourceInfo[];
  }[];
}

interface TextMessageProps {
  message: MessageResponse;
  isFirstUserMessage?: boolean;
  useTitle?: boolean;
}

export const TextMessage = ({ message, isFirstUserMessage, useTitle }: TextMessageProps): React.ReactElement => {
  const { client } = useContext(ChatbotWidgetContext);
  const isEndUser = useMemo(() => message.$userType === 'end_user', [ message ]);

  const agent = useMemo(() => {
    if (!client) return;

    switch (message.$userType) {
    case 'virtual_agent':
      return client.chat?.state.all_virtual_agents.find(agent => agent.id === message.$userId);
    case 'user':
      return client.chat?.state.all_agents.find(agent => agent.id === message.$userId);
    default:
      return;
    }
  }, [ client, message ]);

  const parsedMessageContent = useMemo(() => {
    if (useTitle && typeof message.title === 'string') {
      return <Markdown remarkPlugins={ [ remarkGfm ] }>{message.title}</Markdown>;
    }

    if (typeof message.content === 'string') {
      return <Markdown remarkPlugins={ [ remarkGfm ] }>{
        message.content
      }</Markdown>;
    }

    if (typeof message.content === 'object') {
      const messageContent = message.content as MessageContentWithSources;

      if (messageContent.sources) {
        return <ContentWithSources messageContent={ message.content as MessageContentWithSources } />;
      }

      if (messageContent.indexed_sources) {
        return <ContentWithIndexedSources messageContent={ message.content as MessageContentWithSources } />;
      }
    }
  }, [ isFirstUserMessage, message, useTitle ]);

  return (
    <Box className={ cx(CN, { [`${CN}--end-user`]: isEndUser }) }>
      {!isEndUser && <Avatar src={ agent?.avatar_url } />}
      <Box className={ cx(columnClass, { [`${columnClass}--end`]: isEndUser }) }>
        <Card
          className={ cx(
            cardClass,
            {
              [`${cardClass}--end-user`]: isEndUser,
              [`${cardClass}--agent`]: !isEndUser
            }
          ) }
        >
          {!isEndUser && <Typography variant='small' className={ cx(agentNameClass) }>{agent?.name}</Typography>}
          <Typography
            variant='small'
            className={ cx(textClass, { [`${textClass}--end-user`]: isEndUser }) }
          >
            {parsedMessageContent}
          </Typography>
        </Card>
        <Typography variant='caption' color='textSecondary'>
          {dayjs(message.$timestamp).format('HH:mm A')}
        </Typography>
      </Box>
    </Box>
  );
};
